import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import useClickOutside from '../../../../hooks/useClickOutside';
import { useAuth } from '../../../../hooks/useAuth';
import { supabase } from '../../../../lib/supabase';
import { LogOut, User, Settings, HelpCircle, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import LogoutConfirmationModal from '../../../LogoutConfirmationModal/LogoutConfirmationModal';

const UserAvatar: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const dropdownRef = useClickOutside<HTMLDivElement>(() => setIsOpen(false));

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setIsLogoutModalOpen(false);
    setIsOpen(false);
  };

  const userMenuItems = [
    { name: t('nav.profile_menu'), icon: <User className="size-5" />, path: '/perfil' },
    { name: t('nav.settings_menu'), icon: <Settings className="size-5" />, path: '/configuracoes' },
    { name: t('nav.documentation_menu'), icon: <BookOpen className="size-5" />, path: '/documentacao' },
    { name: t('nav.help_menu'), icon: <HelpCircle className="size-5" />, path: '/ajuda' },
  ];

  const userInitials = user?.email ? user.email.charAt(0).toUpperCase() : '?';

  return (
    <div className="relative" ref={dropdownRef}>
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center aspect-square bg-primary text-white font-bold rounded-full size-10 border-2 border-transparent hover:border-primary transition-colors"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        {userInitials}
      </motion.button>

      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: -10, scale: 0.95 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
          className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
        >
          <div className="p-4 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-semibold text-[#181411] dark:text-white truncate">{user?.email}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">ID: {user?.id.substring(0, 8)}...</p>
          </div>
          <div className="p-2">
            {userMenuItems.map((item, index) => (
              <motion.div
                key={item.name}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.05 }}
                whileHover={{ x: 5 }}
              >
                <Link
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                >
                  {item.icon}
                  {item.name}
                </Link>
              </motion.div>
            ))}

            <motion.button
              onClick={() => {
                setIsOpen(false);
                setIsLogoutModalOpen(true);
              }}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors mt-2 border-t border-gray-100 dark:border-gray-700/50"
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: userMenuItems.length * 0.05 }}
              whileHover={{ x: 5 }}
            >
              <LogOut className="size-5" />
              {t('nav.logout_menu')}
            </motion.button>
          </div>
        </motion.div>
      )}

      <LogoutConfirmationModal
        isOpen={isLogoutModalOpen}
        onClose={() => setIsLogoutModalOpen(false)}
        onConfirm={handleLogout}
      />
    </div>
  );
};

export default UserAvatar;